import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithPopup
} from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, googleProvider } from '../lib/firebase';
import { 
  createSession, 
  endSession, 
  endAllUserSessions, 
  updateSessionActivity, 
  getCurrentSessionId,
  cleanupInactiveSessions 
} from '../lib/sessionService';
import { needsFreshDataOnAppOpen, invalidateAllCache } from '../lib/cacheService';
import { subscribeToGlobalSettings, updateCacheRefreshButtonsSetting, updateConsoleLogsSetting } from '../lib/settingsService';
import { setLoggingEnabled } from '../lib/consoleLogger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCacheRefreshButtons, setShowCacheRefreshButtonsState] = useState(true);
  const [showConsoleLogs, setShowConsoleLogsState] = useState(true);

  // Custom setter that persists to Firestore
  const setShowCacheRefreshButtons = async (value) => {
    try {
      await updateCacheRefreshButtonsSetting(value, currentUser?.email || 'admin');
      // State will be updated automatically via the subscription
    } catch (error) {
      console.error('Error updating cache refresh buttons setting:', error);
      // Fallback: update local state even if Firestore update fails
      setShowCacheRefreshButtonsState(value);
    }
  };

  // Custom setter for console logs that persists to Firestore
  const setShowConsoleLogs = async (value) => {
    try {
      await updateConsoleLogsSetting(value, currentUser?.email || 'admin');
      // State will be updated automatically via the subscription
    } catch (error) {
      console.error('Error updating console logs setting:', error);
      // Fallback: update local state even if Firestore update fails
      setShowConsoleLogsState(value);
      setLoggingEnabled(value);
    }
  };

  // Sign in function
  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Session tracking disabled to reduce Firestore writes
    // Create session on successful login
    // try {
    //   await createSession(userCredential.user.uid, {
    //     loginMethod: 'email',
    //     userAgent: navigator.userAgent
    //   });
    // } catch (error) {
    //   console.error('Error creating session:', error);
    // }
    
    return userCredential;
  };

  // Google sign in function
  const loginWithGoogle = async () => {
    const userCredential = await signInWithPopup(auth, googleProvider);
    
    // Session tracking disabled to reduce Firestore writes
    // Create session on successful login
    // try {
    //   await createSession(userCredential.user.uid, {
    //     loginMethod: 'google',
    //     userAgent: navigator.userAgent
    //   });
    // } catch (error) {
    //   console.error('Error creating session:', error);
    // }
    
    return userCredential;
  };

  // Sign out function
  const logout = async () => {
    // End current session before signing out
    try {
      const currentSessionId = getCurrentSessionId();
      if (currentSessionId) {
        await endSession(currentSessionId);
      }
    } catch (error) {
      console.error('Error ending session:', error);
    }
    
    return signOut(auth);
  };

  // Create user function (for initial admin setup only)
  const createUser = async (email, password, role = 'admin') => {
    // This should only be used for initial admin setup
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Create user document in Firestore with role
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: email,
      role: role,
      createdAt: new Date(),
      status: 'active'
    });
    
    return userCredential;
  };

  // Create admin user with Google sign-in
  const createAdminWithGoogle = async () => {
    const userCredential = await signInWithPopup(auth, googleProvider);
    
    // Create user document in Firestore with admin role
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: userCredential.user.email,
      displayName: userCredential.user.displayName,
      photoURL: userCredential.user.photoURL,
      role: 'admin',
      createdAt: new Date(),
      status: 'active'
    });
    
    // Session tracking disabled to reduce Firestore writes
    // Create session for new admin
    // try {
    //   await createSession(userCredential.user.uid, {
    //     loginMethod: 'google',
    //     userAgent: navigator.userAgent,
    //     isNewUser: true
    //   });
    // } catch (error) {
    //   console.error('Error creating session:', error);
    // }
    
    return userCredential;
  };

  // Create user with invitation (invitation-based registration)
  const createUserWithInvitation = async (email, password, displayName, invitationData) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Create user document in Firestore with role from invitation
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: email,
      displayName: displayName,
      role: invitationData.role,
      createdAt: new Date(),
      status: 'active',
      invitedBy: invitationData.createdBy || null
    });
    
    return userCredential;
  };

  // Get user role and status from Firestore
  const getUserRole = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Check if user is inactive and force logout if needed
        if (userData.status === 'inactive') {
          console.log('User is inactive, forcing logout');
          await logout();
          return null;
        }
        
        return userData.role;
      }
      return null;
    } catch (error) {
      console.error('Error getting user role:', error);
      return null;
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return userRole === 'admin';
  };

  // Check if user can perform action based on role
  const hasPermission = (action) => {
    if (!userRole) return false;
    
    // Admin can do everything
    if (userRole === 'admin') return true;
    
    // Add more role-based permissions here as needed
    const permissions = {
      'solo_lectura': ['orders.view', 'equipment.view', 'clients.view', 'technicians.view'],
      'shopkeeper': ['orders.view', 'orders.edit', 'orders.create', 'equipment.view', 'clients.view', 'clients.create', 'clients.edit', 'technicians.view', 'technicians.create', 'technicians.edit'],
      'admin': ['*'] // All permissions
    };
    
    return permissions[userRole]?.includes(action) || permissions[userRole]?.includes('*');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const role = await getUserRole(user.uid);
        setUserRole(role);
        
        // Only proceed if user wasn't logged out due to deactivation
        if (role) {
          // Check if cache needs refresh on app open
          try {
            if (needsFreshDataOnAppOpen()) {
              invalidateAllCache();
            }
          } catch (error) {
            console.error('Error checking cache freshness on app open:', error);
          }
          
          // Session tracking disabled to reduce Firestore writes
          // Check if user has an active session, if not create one
          // try {
          //   const currentSessionId = getCurrentSessionId();
          //   if (currentSessionId) {
          //     // Update existing session activity
          //     await updateSessionActivity(currentSessionId);
          //   } else {
          //     // Create new session for existing authenticated user
          //     await createSession(user.uid, {
          //       loginMethod: 'existing_auth',
          //       userAgent: navigator.userAgent
          //     });
          //   }
          // } catch (error) {
          //   console.error('Error managing session on auth state change:', error);
          // }
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Subscribe to global settings changes
  useEffect(() => {
    const unsubscribe = subscribeToGlobalSettings((settings) => {
      setShowCacheRefreshButtonsState(settings.showCacheRefreshButtons);
      setShowConsoleLogsState(settings.showConsoleLogs);
      setLoggingEnabled(settings.showConsoleLogs);
    });

    return unsubscribe;
  }, []);

  // Listen for real-time user document changes to detect deactivation
  // Only for admins since they're most likely to be deactivated by other admins
  useEffect(() => {
    if (!currentUser || userRole !== 'admin') return;

    const userDocRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data();
        if (userData.status === 'inactive') {
          console.log('User deactivated in real-time, forcing logout');
          logout();
        }
      }
    }, (error) => {
      console.error('Error listening to user document changes:', error);
    });

    return unsubscribe;
  }, [currentUser, userRole]);

  // Update session activity periodically and validate user status
  useEffect(() => {
    if (!currentUser) return;
    
    // Session tracking disabled to reduce Firestore writes
    // const updateActivity = async () => {
    //   try {
    //     const currentSessionId = getCurrentSessionId();
    //     if (currentSessionId) {
    //       await updateSessionActivity(currentSessionId);
    //     }
    //   } catch (error) {
    //     console.error('Error updating session activity:', error);
    //   }
    // };
    
    const validateUserStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.status === 'inactive') {
            console.log('User has been deactivated, logging out');
            await logout();
          }
        }
      } catch (error) {
        console.error('Error validating user status:', error);
      }
    };
    
    // Session tracking disabled to reduce Firestore writes
    // Update activity every 5 minutes
    // const activityInterval = setInterval(updateActivity, 5 * 60 * 1000);
    
    // Check user status every 10 minutes instead of 30 seconds (reduces reads by 95%)
    // const statusInterval = setInterval(validateUserStatus, 10 * 60 * 1000);
    
    // Clean up inactive sessions every hour (only for admin users to reduce unnecessary calls)
    // const cleanupInterval = userRole === 'admin' ? 
    //   setInterval(() => cleanupInactiveSessions(24), 60 * 60 * 1000) : 
    //   null;
    
    // Update activity on page interaction
    // const handleActivity = () => {
    //   updateActivity();
    // };
    
    // document.addEventListener('click', handleActivity);
    // document.addEventListener('keypress', handleActivity);
    
    // Session tracking disabled to reduce Firestore writes
    // End session when user closes browser/tab
    // const handleBeforeUnload = async () => {
    //   const currentSessionId = getCurrentSessionId();
    //   if (currentSessionId) {
    //     // Use sendBeacon for reliable session ending during page unload
    //     const sessionRef = doc(db, 'user_sessions', currentSessionId);
    //     try {
    //       await updateDoc(sessionRef, {
    //         isActive: false,
    //         endTime: Timestamp.now(),
    //         lastActivity: Timestamp.now()
    //       });
    //     } catch (error) {
    //       console.error('Error ending session on beforeunload:', error);
    //     }
    //   }
    // };
    
    // window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      // Session tracking disabled - no intervals to clear
      // clearInterval(activityInterval);
      // clearInterval(statusInterval);
      // if (cleanupInterval) clearInterval(cleanupInterval);
      // document.removeEventListener('click', handleActivity);
      // document.removeEventListener('keypress', handleActivity);
      // window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentUser, userRole]);

  const value = {
    currentUser,
    userRole,
    login,
    loginWithGoogle,
    logout,
    createUser,
    createAdminWithGoogle,
    createUserWithInvitation,
    isAdmin,
    hasPermission,
    loading,
    showCacheRefreshButtons,
    setShowCacheRefreshButtons,
    showConsoleLogs,
    setShowConsoleLogs
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};